class BlogAiOverview extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.toggleButton = this.querySelector("[data-blog-ai-overview-toggle]");
    this.closeButton = this.querySelector("[data-blog-ai-overview-close]");
    this.action = this.querySelector("[data-blog-ai-overview-action]");
    this.media = this.querySelector("[data-blog-ai-overview-media]");
    this.drawer = this.querySelector("[data-blog-ai-overview-drawer]");
    this.content = this.querySelector("[data-blog-ai-overview-content]");

    if (!this.toggleButton || !this.media || !this.drawer) return;

    this.isInitialized = true;
    this.toggleButton.addEventListener("click", () =>
      this.setOpen(!this.classList.contains("is-open")),
    );
    this.media.addEventListener("click", (event) => {
      if (
        event.target instanceof Element &&
        event.target.closest("[data-blog-ai-overview-close]")
      ) {
        return;
      }
      this.setOpen(!this.classList.contains("is-open"));
    });
    this.closeButton?.addEventListener("click", (event) => {
      event.stopPropagation();
      this.setOpen(false, true);
    });
    this.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.classList.contains("is-open")) {
        this.setOpen(false, true);
      }
    });

    if (this.dataset.blogAiOverviewStatus === "ready") {
      this.observeViewportPeek();
    } else if (this.dataset.blogAiOverviewStatus === "missing") {
      this.requestOverview();
    }
  }

  async requestOverview() {
    const endpoint = this.dataset.blogAiOverviewEndpoint;
    const articleId = this.dataset.blogAiOverviewArticleId;

    if (!endpoint || !articleId || !this.content) return;

    this.dataset.blogAiOverviewStatus = "generating";

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ articleId }),
      });
      const payload = await response.json();

      if (response.status === 202 || payload.status === "generating") {
        return;
      }

      if (!response.ok || payload.status !== "ready" || !payload.overview?.html) {
        throw new Error("AI overview request failed");
      }

      // The app sanitizes this fragment against a strict formatting-only allowlist before returning it.
      this.content.innerHTML = payload.overview.html;
      this.dataset.blogAiOverviewStatus = "ready";
      this.action?.removeAttribute("hidden");
      this.drawer.removeAttribute("hidden");
      this.observeViewportPeek();
    } catch (_error) {
      this.dataset.blogAiOverviewStatus = "missing";
    }
  }

  observeViewportPeek() {
    if (this.hasObservedViewportPeek) return;
    this.hasObservedViewportPeek = true;

    if (!("IntersectionObserver" in window)) {
      this.classList.add("has-viewport-peek");
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.classList.add("has-viewport-peek");
        observer.disconnect();
      },
      { threshold: 0.6 },
    );

    observer.observe(this.toggleButton);
  }

  setOpen(isOpen, returnFocus = false) {
    if (isOpen && this.dataset.blogAiOverviewStatus !== "ready") return;

    this.classList.toggle("is-open", isOpen);
    this.toggleButton.setAttribute("aria-expanded", String(isOpen));
    this.drawer.setAttribute("aria-hidden", String(!isOpen));

    if (isOpen) {
      this.drawer.removeAttribute("inert");
    } else {
      this.drawer.setAttribute("inert", "");
      if (returnFocus) this.toggleButton.focus();
    }
  }
}

if (!customElements.get("blog-ai-overview")) {
  customElements.define("blog-ai-overview", BlogAiOverview);
}
